import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Grid, Typography } from 'components/base';
import dataApi from 'data/api/data';
import useDataContext from 'data/context/data';
import produce from 'utils/produce';
import ScriptLoader from 'react-script-loader-hoc';
import { formatPrice } from 'utils/format';
import sleep from 'utils/sleep';
import { getOriginalPage, getPageSize, getTemplatePage } from 'editor-utils/template';
import { formatData } from 'editor-utils/dataParser';

import { generatePage } from '../generator/utils';
import pagesValidator from '../lib/pagesValidator';
import PageWrapper from '../components/page/wrapper';
import Page from '../components/page/preview';
import withPlaceholderStyle from '../components/placeholder';

const CAPTION = {
  'return-address': 'Return Address Only',
  'recipient-address': 'Return & Recipient Address',
};

const isPartOfAddressDefined = value => {
  if (value == null || value.split(' ').join('') === '') {
    return false;
  }
  return true;
};

const renderRecipientAddress = address => {
  const rows = [];
  if (isPartOfAddressDefined(address.name)) {
    rows.push(address.name);
  }
  if (isPartOfAddressDefined(address.addr1)) {
    rows.push(address.addr1);
  }
  if (isPartOfAddressDefined(address.addr2)) {
    rows.push(address.addr2);
  }

  const subRow = [];
  if (isPartOfAddressDefined(address.city)) {
    subRow.push(address.city);
  }
  if (isPartOfAddressDefined(address.state)) {
    if (subRow.length > 0) {
      subRow[0] = `${subRow[0]},`;
    }
    subRow.push(address.state);
  }
  if (isPartOfAddressDefined(address.zip)) {
    subRow.push(address.zip);
  }
  if (subRow.length > 0) {
    rows.push(subRow.join(' '));
  }

  if (isPartOfAddressDefined(address.country)) {
    if (['USA'].indexOf(address.country.toUpperCase()) < 0) {
      rows.push(address.country);
    }
  }

  return rows.join('\n');
};

const CustomPlaceholder = withPlaceholderStyle(
  ({
    quantity,
    placeholder,
    csvBox,
    returnAddress,
    recipientAddresses,
    onReturnAddressChange,
    onRecipientAddressesChange,
  }) => {
    if (placeholder.id === 'address-return') {
      return (
        <div
          style={{ left: `2.5%`, top: '5%', width: '95%', height: '40%', maxWidth: 350 }}
          className="editor-placeholder"
        >
          <Form.Label className="text-left">Add a return address</Form.Label>
          <Form.Textarea
            defaultValue={returnAddress}
            onChange={e => {
              onReturnAddressChange(e.target.value);
            }}
            rows={5}
          />
        </div>
      );
    }

    if (placeholder.id === 'address-recipient') {
      const alreadyImported = recipientAddresses.length > 0;

      return (
        <div
          style={{ left: `2.5%`, top: '45%', width: '95%', height: '50%' }}
          className="editor-placeholder d-flex flex-column"
        >
          <Form.Label className="text-left">Add Recipients [Optional]</Form.Label>
          <Form.Label className="text-left label-light">
            If you also want to print recipient addresses on your envelopes - start by importing
            them.
          </Form.Label>
          <div
            className="d-flex flex-column"
            style={{
              border: '1px solid #c7cdd8',
              borderRadius: '0.5rem',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {alreadyImported ? (
              <Button
                type="danger"
                onClick={() => {
                  onRecipientAddressesChange([]);
                }}
              >
                Clear Recipient Addresses
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={csvBox == null}
                onClick={() => {
                  csvBox.openModal();
                }}
              >
                Import Recipient Addresses
              </Button>
            )}

            <Typography.P className="mt-1 text-black">
              {alreadyImported && (
                <Typography.H6>
                  You&apos;ve successfully imported {recipientAddresses.length} addresses
                </Typography.H6>
              )}
              If you&apos;d like to add more than {quantity} addresses, please change your quantity
              above{alreadyImported && <> and re-upload all addresses again</>}.
            </Typography.P>
          </div>
        </div>
      );
    }

    if (placeholder.id === 'empty-envelope') {
      return (
        <div
          className="editor-placeholder d-flex align-items-center justify-content-center flex-column"
          style={{ left: 0, top: 0, width: '100%', height: '100%' }}
        >
          <Typography.H6>Envelope addressing was not selected</Typography.H6>
          <Typography.P>
            Start over and add addressing to your order for just {formatPrice(0.3, 'USD')} per
            envelope
          </Typography.P>
        </div>
      );
    }

    return null;
  },
);

const EnvelopeEditor = props => {
  const dataContext = useDataContext();

  const product = useMemo(() => {
    return dataContext.getSkuProduct(props.productId);
  }, [props.productId]);

  const [template, setTemplate] = useState();

  const [selectedPage, setSelectedPage] = useState(props.data.additional_info?.pageId ?? null);
  const [returnAddress, setReturnAddress] = useState(
    props.data.additional_info?.returnAddress ?? '',
  );
  const [recipientAddresses, setRecipientAddresses] = useState(
    props.data.additional_info?.recipientAddresses ?? [],
  );

  const [csvBox, setCsvBox] = useState();

  useEffect(() => {
    if (props.scriptsLoadedSuccessfully) {
      (async () => {
        await sleep(500);
        // eslint-disable-next-line no-undef
        const instance = new CSVBoxImporter(
          'GPjBYh0fW65Pfd0ZwtxW1hbfmWHHv9',
          {},
          (result, data) => {
            if (result) {
              setRecipientAddresses(data.rows);
            }
          },
        );
        instance.setUser({
          user_id: 'default123',
        });
        instance.setUpImporter();
        instance.listen('onReady', () => {
          setCsvBox(instance);
        });
      })();
    }
  }, [props.scriptsLoadedSuccessfully]);

  useEffect(() => {
    if (csvBox && props.quantity) {
      csvBox.setOptions({
        max_rows: props.quantity,
      });
    }
  }, [csvBox, props.quantity]);

  useEffect(() => {
    (async (sku, version) => {
      const { data, error } = await dataApi.getTemplate(sku, version);
      if (data) {
        setTemplate(data);
        if (data.pages) {
          setSelectedPage(val => {
            if (data.pages.find(page => page.id === val)) {
              return val;
            }
            return data.pages[0].id;
          });
        } else {
          setSelectedPage(null);
        }
      }
    })(props.productId, props.version);
  }, [props.productId, props.version]);

  useEffect(() => {
    if (template && template.pages) {
      let quantity;
      let numberOfPages;
      const data = {
        pages: [],
        additionalInfo: {
          pageId: selectedPage,
          returnAddress,
          recipientAddresses,
        },
      };

      if (recipientAddresses.length > 0) {
        quantity = 1;
        numberOfPages = props.quantity;
      } else {
        quantity = undefined;
        numberOfPages = 1;
      }

      const page = getTemplatePage(template, selectedPage);
      if (page) {
        for (let index = 0; index < numberOfPages; index += 1) {
          const value = generatePage(page);

          data.pages.push(
            produce(value, draft => {
              const returnAddressIndex = value.placeholders.findIndex(
                p => p.id === 'address-return',
              );
              if (returnAddressIndex >= 0) {
                draft.placeholders[returnAddressIndex].data.value = returnAddress;
              }

              if (index < recipientAddresses.length) {
                const recipientAddressIndex = value.placeholders.findIndex(
                  p => p.id === 'address-recipient',
                );
                if (recipientAddressIndex >= 0) {
                  draft.placeholders[recipientAddressIndex].data.value = renderRecipientAddress(
                    recipientAddresses[index],
                  );
                }
              }
            }),
          );
        }
      }

      const validator = pagesValidator(template, data.pages);
      props.onChange({
        quantity,
        data: {
          ...formatData(template, data, { validator }),
          favorite: undefined,
          photos: undefined,
        },
      });
    }
  }, [props.quantity, selectedPage, template, returnAddress, recipientAddresses]);

  const originalPage = useMemo(() => {
    if (!template || !selectedPage) {
      const size = getPageSize(props.template);
      return {
        size: { width: Math.max(...size), height: Math.min(...size) },
        placeholders: [{ id: 'empty-envelope' }],
      };
    }
    return getOriginalPage(template, generatePage(getTemplatePage(template, selectedPage)));
  }, [props.template, selectedPage, template]);

  if (!template || !product) {
    return null;
  }

  return (
    <Grid.Col xs={12} className={props.active ? 'col-page-preview' : 'd-none'}>
      <PageWrapper
        page={originalPage}
        buttonPosition="right"
        otherPages={(template.pages ?? []).map(otherPage => ({
          id: otherPage.id,
          preview: <Typography.H6 className="p-1 m-0">{CAPTION[otherPage.id]}</Typography.H6>,
        }))}
        onChangePage={id => setSelectedPage(id)}
        fullWidth
        otherPagesLabel="OPTIONS"
      >
        <Page page={originalPage} template={props.template} fullWidth renderChildrenOnly>
          {originalPage.placeholders.map(placeholder => (
            <CustomPlaceholder
              key={placeholder.id}
              quantity={props.quantity}
              page={originalPage}
              placeholder={placeholder}
              csvBox={csvBox}
              returnAddress={returnAddress}
              onReturnAddressChange={setReturnAddress}
              recipientAddresses={recipientAddresses}
              onRecipientAddressesChange={setRecipientAddresses}
            />
          ))}
        </Page>
      </PageWrapper>
      <Typography.H6>Envelope</Typography.H6>
    </Grid.Col>
  );
};

EnvelopeEditor.propTypes = {
  productId: PropTypes.string,
  version: PropTypes.string,
  data: PropTypes.object,
  active: PropTypes.bool,
  quantity: PropTypes.number,
  onChange: PropTypes.func,
};

EnvelopeEditor.defaultProps = {
  active: false,
  quantity: 1,
  data: {},
  onChange: () => {},
};

export default ScriptLoader('https://js.csvbox.io/script.js')(EnvelopeEditor);
